import type { ImageOutputFormat, ImageProcessingOptions, ProcessedFile } from '../../types';

const extensionByMime: Record<ImageOutputFormat, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/avif': 'avif',
};

export class ImageProcessor {
  public async process(file: File, options: ImageProcessingOptions): Promise<ProcessedFile> {
    const bitmap = await createImageBitmap(file);
    const crop = options.crop ?? {
      x: 0,
      y: 0,
      width: bitmap.width,
      height: bitmap.height,
    };

    const targetWidth = this.normalizeDimension(options.resizeWidth, crop.width);
    const targetHeight = this.normalizeDimension(options.resizeHeight, crop.height);
    const canvas = this.createCanvas(targetWidth, targetHeight);
    const context = canvas.getContext('2d', { alpha: options.outputFormat !== 'image/jpeg' });

    if (!context) {
      throw new Error('Canvas 2D context is not available in this browser.');
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
      bitmap,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      targetWidth,
      targetHeight,
    );

    bitmap.close();
    const blob = await this.toBlob(canvas, options.outputFormat, options.quality);

    return {
      blob,
      fileName: this.getOutputName(file.name, options.outputFormat),
      size: blob.size,
      mimeType: blob.type,
    };
  }

  private normalizeDimension(value: number | undefined, fallback: number): number {
    const normalized = Math.round(value ?? fallback);
    return Math.max(1, normalized);
  }

  private createCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  private toBlob(canvas: HTMLCanvasElement, mimeType: ImageOutputFormat, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error(`The browser failed to encode ${mimeType}.`));
            return;
          }

          resolve(blob);
        },
        mimeType,
        quality,
      );
    });
  }

  private getOutputName(inputName: string, mimeType: ImageOutputFormat): string {
    const baseName = inputName.replace(/\.[^.]+$/, '');
    return `${baseName || 'image'}.${extensionByMime[mimeType]}`;
  }
}
